/**
 * Bill Payment Operations - Sistema de Pagamento de Fatura SEGURO
 * 
 * MODELO:
 * - Cria transação de débito na conta para pagamento da fatura
 * - Marca transações do cartão como pagas via FIFO
 * - Libera limite do cartão proporcionalmente
 * 
 * MELHORIAS DE SEGURANÇA:
 * - Callback de progresso para UI
 * - Retry automático com backoff exponencial
 * - Validação robusta antes de processar
 * - Salvamento único via batch (sem duplicação)
 */

import { Transaction } from '@/types';
import { 
  applyFIFOPayment, 
  generatePaymentSummary,
} from '@/utils/fifoPaymentUtils';
import { logger } from '@/utils/logger';
import { v4 as uuidv4 } from 'uuid';
import { saveTransactionsBatchToSupabaseWithRetry } from '@/lib/supabase-operations';

export interface BillPaymentProgress {
  step: 'validating' | 'calculating' | 'saving' | 'releasing' | 'updating' | 'completed' | 'error';
  progress: number; // 0-100
  message: string;
  transactionsCount?: number;
  estimatedTimeMs?: number;
  errorMessage?: string;
  errorStep?: string;
}

export interface BillPaymentParams {
  cardId: string;
  cardName: string;
  amount: number;
  accountId: string;
  date: string;
  billTransactions: Transaction[];
  transactions: Transaction[];
  releaseCardLimit: (cardId: string, amount: number) => void;
  setTransactions: React.Dispatch<React.SetStateAction<Transaction[]>>;
  onPaymentSummary?: (summary: any) => void;
  onProgress?: (progress: BillPaymentProgress) => void;
  billPeriod?: string;
}

/**
 * Extrai o billPeriod das transações da fatura
 */
const extractBillPeriod = (billTransactions: Transaction[]): string | undefined => {
  const existingBillPeriod = billTransactions.find(t => t.billPeriod)?.billPeriod;
  if (existingBillPeriod) return existingBillPeriod;
  
  const firstTransaction = billTransactions[0];
  if (!firstTransaction) return undefined;
  
  const dateStr = firstTransaction.billDueDate || firstTransaction.date;
  if (!dateStr) return undefined;
  
  // CORRIGIDO: billPeriod = mês ANTERIOR ao vencimento (consistente com gráfico)
  // Ex: vencimento 2026-01-03 → billPeriod 2025-12
  const date = new Date(dateStr + 'T12:00:00');
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Estima tempo de processamento baseado no número de transações
 */
const estimateProcessingTime = (transactionCount: number): number => {
  // Base: 500ms + 50ms por transação (criptografia) + 1000ms (save)
  return 500 + (transactionCount * 50) + 1000;
};

export const processBillPayment = async (params: BillPaymentParams): Promise<boolean> => {
  const {
    cardId,
    cardName,
    amount,
    accountId,
    date,
    billTransactions,
    transactions,
    releaseCardLimit,
    setTransactions,
    onPaymentSummary,
    onProgress,
    billPeriod: providedBillPeriod
  } = params;

  const reportProgress = (progress: BillPaymentProgress) => {
    logger.debug(`Progress: ${progress.step} - ${progress.progress}%`);
    onProgress?.(progress);
  };

  try {
    // STEP 1: Validação (0-15%)
    reportProgress({
      step: 'validating',
      progress: 0,
      message: 'Validando dados da fatura...',
    });

    await new Promise(resolve => setTimeout(resolve, 200)); // Visual feedback

    if (!cardId || !accountId || amount <= 0) {
      throw new Error('Dados inválidos: cartão, conta ou valor incorreto');
    }

    // CORRIGIDO: Filtrar por dívida restante > 0, não apenas status
    // Isso garante que transações parcialmente pagas sejam incluídas corretamente
    // CORRIGIDO: Excluir pagamentos de fatura do FIFO (eles têm cardId mas não são compras)
    const unpaidBillTransactions = billTransactions.filter(t => {
      if (t.cardId !== cardId || t.type !== 'despesa' || t.isBillPayment) return false;
      
      const originalAmount = t.originalAmount || Math.abs(t.amount);
      const paidPortion = t.paidPortion || 0;
      const remainingDebt = originalAmount - paidPortion;
      
      return remainingDebt > 0;
    });

    if (unpaidBillTransactions.length === 0) {
      throw new Error('Nenhuma transação pendente encontrada para pagamento');
    }

    reportProgress({
      step: 'validating',
      progress: 15,
      message: `${unpaidBillTransactions.length} transações pendentes encontradas`,
      transactionsCount: unpaidBillTransactions.length,
      estimatedTimeMs: estimateProcessingTime(unpaidBillTransactions.length),
    });

    // STEP 2: Cálculo FIFO (15-35%)
    reportProgress({
      step: 'calculating',
      progress: 20,
      message: 'Calculando distribuição FIFO...',
      transactionsCount: unpaidBillTransactions.length,
      estimatedTimeMs: estimateProcessingTime(unpaidBillTransactions.length),
    });

    const fifoResult = applyFIFOPayment(unpaidBillTransactions, amount);
    const paymentSummary = generatePaymentSummary(fifoResult);

    logger.info('Resultado FIFO:', {
      totalApplied: fifoResult.totalApplied,
      transacoesAfetadas: fifoResult.appliedTransactions.length,
      remainingPayment: fifoResult.remainingPayment,
    });

    const billPeriod = providedBillPeriod || extractBillPeriod(unpaidBillTransactions);

    reportProgress({
      step: 'calculating',
      progress: 35,
      message: `Pagamento de ${fifoResult.appliedTransactions.length} transações calculado`,
      transactionsCount: fifoResult.appliedTransactions.length,
    });

    // STEP 3: Criar transação de pagamento e salvar (35-70%)
    reportProgress({
      step: 'saving',
      progress: 40,
      message: 'Preparando transações para salvamento...',
    });

    // Extrair billDueDate das transações da fatura para correlação robusta
    const billDueDate = unpaidBillTransactions.find(t => t.billDueDate)?.billDueDate ||
                        billTransactions.find(t => t.billDueDate)?.billDueDate;

    const billPaymentTransaction: Transaction = {
      id: uuidv4(),
      type: 'despesa',
      amount: amount,
      description: `Pagamento de fatura - ${cardName}`,
      date: date,
      account: '',
      accountId: accountId,
      categoryId: '',
      paymentMethod: 'transferencia',
      status: 'efetivada',
      isBillPayment: true,
      // SOLUÇÃO DEFINITIVA: pagamento de fatura NÃO deve ter cardId (senão cai em filtros do cartão)
      cardId: undefined,
      billCardId: cardId,
      billPeriod: billPeriod,
      // NOVO: billDueDate para correlação robusta (chave primária da fatura)
      billDueDate: billDueDate,
    };

    const allTransactionsToSave = [
      billPaymentTransaction,
      ...fifoResult.updatedTransactions
    ];

    reportProgress({
      step: 'saving',
      progress: 50,
      message: `Salvando ${allTransactionsToSave.length} transações...`,
      transactionsCount: allTransactionsToSave.length,
    });

    // Salvar com retry automático
    await saveTransactionsBatchToSupabaseWithRetry(allTransactionsToSave, 3);

    reportProgress({
      step: 'saving',
      progress: 70,
      message: 'Transações salvas com sucesso!',
    });

    // STEP 4: Liberar limite do cartão (70-85%)
    reportProgress({
      step: 'releasing',
      progress: 75,
      message: 'Liberando limite do cartão...',
    });

    const limitToRelease = fifoResult.totalApplied;
    
    if (limitToRelease > 0) {
      releaseCardLimit(cardId, limitToRelease);
      logger.info(`Limite liberado: ${limitToRelease}`);
    }

    reportProgress({
      step: 'releasing',
      progress: 85,
      message: `Limite de R$ ${limitToRelease.toFixed(2)} liberado`,
    });

    // STEP 5: Atualizar estado local (85-100%)
    reportProgress({
      step: 'updating',
      progress: 90,
      message: 'Atualizando dados locais...',
    });

    // Atualizar transações no estado local (SEM salvar novamente!)
    setTransactions(prev => {
      const nonBillTransactions = prev.filter(t => 
        !unpaidBillTransactions.some(bt => bt.id === t.id)
      );
      
      return [
        ...nonBillTransactions, 
        ...fifoResult.updatedTransactions,
        billPaymentTransaction
      ];
    });

    // Chamar callback de resumo se fornecido
    if (onPaymentSummary) {
      onPaymentSummary({
        fifoResult,
        paymentSummary,
        cardId,
        amount,
        cardName,
        billPeriod,
        success: true
      });
    }

    // STEP 6: Concluído (100%)
    reportProgress({
      step: 'completed',
      progress: 100,
      message: 'Pagamento processado com sucesso!',
    });

    logger.info('Pagamento de fatura concluído:', {
      totalApplied: fifoResult.totalApplied,
      fullyPaidCount: paymentSummary.fullyPaidCount,
      partiallyPaidCount: paymentSummary.partiallyPaidCount,
      paymentTransactionId: billPaymentTransaction.id,
      billPeriod
    });
    
    return true;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
    logger.error('Erro ao processar pagamento de fatura:', error);
    
    reportProgress({
      step: 'error',
      progress: 0,
      message: 'Falha no processamento',
      errorMessage: errorMessage,
      errorStep: 'saving',
    });
    
    throw error;
  }
};

export const getBillPaymentStatus = (transactions: Transaction[], cardId: string, billPeriod: string) => {
  // CORRIGIDO: Filtrar transações pelo billPeriod fornecido
  // Isso evita misturar transações de diferentes faturas
  const billTransactions = transactions.filter(t => {
    if (t.cardId !== cardId || t.type !== 'despesa' || t.isBillPayment) return false;
    
    // Se a transação tem billPeriod, usar ele
    if (t.billPeriod) {
      return t.billPeriod === billPeriod;
    }
    
    // Fallback: inferir billPeriod da data (transações antigas sem billPeriod)
    // Considerar transações sem billPeriod se não houver como inferir
    return true; // TODO: Implementar inferência de billPeriod se necessário
  });
  
  const totalBillAmount = billTransactions.reduce((sum, t) => sum + (t.originalAmount || Math.abs(t.amount)), 0);
  const totalPaidAmount = billTransactions.reduce((sum, t) => sum + (t.paidPortion || 0), 0);
  const remainingAmount = Math.max(0, totalBillAmount - totalPaidAmount);
  
  let status: 'pending' | 'partial' | 'paid' = 'pending';
  if (totalPaidAmount >= totalBillAmount && totalBillAmount > 0) {
    status = 'paid';
  } else if (totalPaidAmount > 0) {
    status = 'partial';
  }
  
  return {
    totalAmount: totalBillAmount,
    paidAmount: totalPaidAmount,
    remainingAmount,
    status,
    billTransactions,
    paymentTransactions: []
  };
};
